/**
 * Client-driven enquiry status transitions — a STOPGAP for ask `D13`, not where this belongs.
 *
 * Nothing on the server advances an enquiry when its comparison is saved or a quotation is
 * created from it, so the Requirements board shows it as still out at RFQ — or, for a
 * contract-priced provision enquiry that never had an RFQ at all, as still `Open`, with its card
 * opening the RFQ builder while a fully-priced quotation sits on `/quote/:id`. Measured
 * 2026-08-08: `ENQ-2026-00013` carried a fully-priced draft quotation (`QTN-2026-00004`,
 * AED 210.45, built 20:54) while its status still read `RFQ Sent` from 14:39. Writing through
 * `enquiry.update_enquiry` persists, so every user sees the same board — which is the
 * requirement, and the reason this could not be fixed on the client's own copy of the data.
 *
 * This is the same thing `useEnquiryMutations.ts` has always done for `RFQ Sent` after
 * `generate_rfqs`; it is not a new pattern. Both should be deleted when D13 ships.
 *
 * Two deliberate choices:
 *   - `shouldAdvanceTo` gates it, so re-saving a comparison (or re-pricing a draft quotation) on
 *     a quoted or decided enquiry cannot drag it backwards for everyone. See `lib/statusFlow.ts`.
 *   - The write is best-effort and its failure is SWALLOWED. The document it accompanies already
 *     saved; failing the whole mutation here would tell the purchaser their split or quotation
 *     did not save when it did, and send them round again. A stale status is the lesser fault —
 *     it is exactly the fault that existed before this code, and the next save retries it.
 */

import { callPut } from './bosun'
import { buildUpdateEnquiryWireBody } from './useEnquiryMutations'
import { shouldAdvanceTo } from '../statusFlow'
import type { EnquiryStatus } from '../types'

/** Move the enquiry to `Comparison Done`. Returns whether a write was made and landed. */
export async function advanceToComparisonDone(enquiryId: string, currentStatus: EnquiryStatus): Promise<boolean> {
  if (!shouldAdvanceTo(currentStatus, 'Comparison Done')) return false
  try {
    await callPut('enquiry', 'update_enquiry', {
      name: enquiryId,
      data: buildUpdateEnquiryWireBody({ status: 'Comparison Done' }),
    })
    return true
  } catch {
    return false
  }
}
